/**
 * SHOW-E-13-01. A signed-out deep link survives the IdP login.
 *
 * The IdP matches `redirect_uri` exactly against the client's registered list, and only the origin root is
 * registered — so a deep URL sent as `redirect_uri` is refused and the link is lost. Instead the webclient
 * always authorizes with one fixed callback (`callbackUri()`) and carries the requested path behind a
 * single-use nonce in the OAuth `state`. Only the nonce goes over the wire; the path stays in this tab's
 * `sessionStorage` until the code exchange reads it back.
 */

import { SQA_RUN_PARAM, SQA_SIGN_IN_HINTS } from './sqa-run-mode';

const RETURN_KEY_PREFIX = 'oauth-return:';
// ITP-E-434-04: `idp_login` rides with the code after a sign-in on an identity page; index.html reads it before first paint.
const OAUTH_PARAMS = ['code', 'state', 'rlogin', 'idp_login'];

/** The callback the IdP has registered for this client: the app's origin root. */
export function callbackUri(loc: Location = window.location): string {
  // Same upgrade the initializer always applied: a non-localhost http page authorizes over https.
  if (loc.protocol === 'http:' && loc.hostname !== 'localhost') {
    return 'https://' + loc.host;
  }
  return loc.origin;
}

/**
 * A same-origin path, or `/`. Must start with a single `/` — no `//` (protocol-relative), no scheme,
 * no backslash (browsers read `/\host` as `//host`), no control characters.
 */
export function sanitizeReturnPath(url: string): string {
  if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) {
    return '/';
  }
  if (url.includes('\\') || /[\u0000-\u001f\u007f]/.test(url)) {
    return '/';
  }
  return url;
}

/**
 * The page the visitor asked for, with the OAuth round-trip params (`code`, `state`, `rlogin`, `idp_login`) stripped. TSP-E-157-28:
 * an SQA run's page (`sqa-run` on the address) drops its sign-in hints too — they ride the authorize instead.
 */
export function currentReturnPath(loc: Location = window.location): string {
  const params = new URLSearchParams(loc.search);
  OAUTH_PARAMS.forEach(name => params.delete(name));
  if (params.has(SQA_RUN_PARAM)) {
    SQA_SIGN_IN_HINTS.forEach(name => params.delete(name));
  }
  const query = params.toString();
  return sanitizeReturnPath(loc.pathname + (query ? '?' + query : '') + loc.hash);
}

function newNonce(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Persists the return path under a fresh nonce and returns the nonce — the value to send as `state`. */
export function buildAuthorizeState(returnPath: string, storage: Storage = window.sessionStorage): string {
  const nonce = newNonce();
  storage.setItem(RETURN_KEY_PREFIX + nonce, sanitizeReturnPath(returnPath));
  return nonce;
}

/** The path stored for this `state`, cleared on read (single use). An unknown or missing nonce yields `/`. */
export function readReturnPath(state: string, storage: Storage = window.sessionStorage): string {
  if (!state) {
    return '/';
  }
  const key = RETURN_KEY_PREFIX + state;
  const path = storage.getItem(key);
  storage.removeItem(key);
  return path === null ? '/' : sanitizeReturnPath(path);
}

/**
 * The configured authorize link with exactly one `redirect_uri` and one `state`. The configured link may
 * already carry a static value for either; they are replaced, never appended to.
 *
 * TSP-E-157-28: `hints` (an SQA run's `sqaRunSignInHints`) pre-fill the IdP's login form. Only the allow-listed
 * names (SQA_SIGN_IN_HINTS) with a value are appended; anything else in `hints` is ignored.
 */
export function buildAuthorizeUrl(authorizeLink: string, redirectUri: string, state: string,
                                  freshLogin = false, hints: Record<string, string> = {}): string {
  const url = new URL(authorizeLink);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', state);
  // TSP-E-157-26: an SQA run signs in as whoever the tester picks — the standard OIDC ask for the login form even
  // when the IdP still holds a session of its own.
  if (freshLogin) {
    url.searchParams.set('prompt', 'login');
  }
  for (const name of SQA_SIGN_IN_HINTS) {
    const value = hints && hints[name];
    if (typeof value === 'string' && value) {
      url.searchParams.set(name, value);
    }
  }
  return url.toString();
}

/**
 * TSP-E-157-24. Where the address goes once a code exchange has signed in: the page saved under `state`, else
 * the current address without the OAuth params. An SQA run's authorize (TSP-E-157-26) saved `/?sqa-run=<key>`.
 */
export function addressAfterSignIn(state: string | null, loc: Location = window.location,
                                   storage: Storage = window.sessionStorage): string {
  const saved = readReturnPath(state, storage);
  return saved !== '/' ? saved : currentReturnPath(loc);
}

/** What the start-up sign-in does next with the token the store holds (see `nextSignInStep`). */
export type SignInStep = 'land' | 'exchange' | 'wait' | 'cookie' | 'dummy' | 'authorize';

export interface SignInInputs {
  hasAccessToken: boolean;
  /** The `?code=` on the address, if any. */
  code: string | null;
  /** The code has been sent to the token endpoint already. */
  codeSent: boolean;
  hasCookiedToken: boolean;
  /** `AllowDummyToken` on a non-production build. */
  dummyTokenAllowed: boolean;
  /** TSP-E-157-26: SQA mode — a `sqa-run` key on the address or in the session. */
  sqaRun?: boolean;
  /** TSP-E-157-26: this tab already signed in for that run through the authorize (`isSqaRunSignedIn`). */
  sqaRunSignedIn?: boolean;
}

/**
 * TSP-E-157-24. The order the start-up sign-in takes a token in. A `?code=` is ALWAYS exchanged, and before a
 * cookied or dummy token. It is exchanged once — a refused code leaves the app unauthorized (the platform's sign-in
 * failure), never silently in the dummy company. With no code: the cookie, then the dummy token, then the authorize
 * redirect.
 *
 * TSP-E-157-26: SQA mode never takes the dummy token, and reuses a cookied session only when this tab signed in for
 * the run already; anything else is a fresh standard authorize, where the tester picks who the run signs in as.
 */
export function nextSignInStep(s: SignInInputs): SignInStep {
  if (s.hasAccessToken) {
    return 'land';
  }
  if (s.code) {
    return s.codeSent ? 'wait' : 'exchange';
  }
  if (s.sqaRun) {
    return s.sqaRunSignedIn && s.hasCookiedToken ? 'cookie' : 'authorize';
  }
  if (s.hasCookiedToken) {
    return 'cookie';
  }
  return s.dummyTokenAllowed ? 'dummy' : 'authorize';
}
